import React, {
    useContext,
    useEffect,
    useState,
    useRef
} from 'react';
import { useNavigate, useParams } from "react-router";
import { useQuery, useMutation } from '@tanstack/react-query';

import { refetchBasketData, NotificationContext, handleError } from '../router'

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";

import InstanceEventSummary from '../components/instance-event-summary';
import PurchaseByPrice from "./purchase-by-price";
import TicketQuantityButtons from "../components/ticket-quantity-buttons";
import BestAvailableButton from './best-available-button';
import LoadingIndicator from '../components/loading-indicator';
import CheckForQueue from './check-for-queue';

let unreserved_ticketType_ID = null;

export default function BookOnline({ checkedForQueue, setCheckedForQueue }) {

    const {notification, setNotification} = useContext(NotificationContext);
    const { instanceId } = useParams();
    let navigate = useNavigate();

    const [isReserved, setIsReserved] = useState(null);
    const [ticketQuantity, setTicketQuantity] = useState(1);
    const [bandsByPrice, setBandsByPrice] = useState(null);
    const [maxTickets, setMaxTickets] = useState(null);

    const addTickets = useMutation({
        mutationFn: () => QUERIES.addTickets(ticketQuantity, unreserved_ticketType_ID),
        onError: (error) => {
            if (error.errorCode === 43 && error.instanceLimits?.length) {
                const limit = error.instanceLimits[0].maxTickets;
                setMaxTickets(limit);
                setTicketQuantity(limit);
            }
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            navigate(`/book-online/${instanceId}/edit-tickets`);
            refetchBasketData();
        },
    })

    const { data: planData } = useQuery({
        queryKey: ['planData', CONSTANTS.PLAN_ID],
        queryFn: QUERIES.getPlanData,
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const {
        data: priceListData,
    } = useQuery({
        queryKey: ["priceListData", CONSTANTS.INSTANCE_ID],
        queryFn: () => QUERIES.getInstancePriceList(CONSTANTS.INSTANCE_ID),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
        enabled: !isReserved
    });

    const {
        data: bandsData,
    } = useQuery({
        queryKey: ["bandsData", CONSTANTS.PLAN_ID],
        queryFn: QUERIES.getBands,
        staleTime: 86400000,
        enabled: CONSTANTS.HAS_BEST_AVAILABLE
    });

    useEffect(() => {
        if (!bandsData || !checkedForQueue) return;

        // Get the band id's from bandData and add it to bandsByPrice to be used by addBestAvailable()
        const updatedBandsArray = UTILS.getBandsByPrice(bandsData);
        setBandsByPrice(updatedBandsArray);
    }, [bandsData, checkedForQueue]);

    useEffect(() => {
        if (!planData || !checkedForQueue) return;

        if (planData.type == "Unreserved") {
            setIsReserved(false);

            if (priceListData) {
                // Check for priceband that is unreserved and the default ticket type
                let default_ticket = priceListData.find((price) => price.priceBand.name == "Unreserved" && price.ticketType?.name == CONSTANTS.DEFAULT_TICKET_TYPE);

                if (!default_ticket) {
                    // If "Unreserved" name doesn't exist just look for default ticket type
                    default_ticket = priceListData.find((price) => price.ticketType?.name == CONSTANTS.DEFAULT_TICKET_TYPE);
                }

                // If no default ticket type just return the first in the array of prices
                unreserved_ticketType_ID = default_ticket ? default_ticket.ticketType?.id : priceListData[0].ticketType?.id;
            }
            
        } else {
            setIsReserved(true);

            if (!CONSTANTS.HAS_BEST_AVAILABLE) {
                navigate(`/book-online/${instanceId}/select-seats`);
            }
        }
    }, [planData, priceListData, checkedForQueue]);

    // Check to see if this event needs a queue by rendering the chooseseats iframe.
    // If the iframe goes to chooseSeats.aspx load the rest of the component
    if (!checkedForQueue) {
        return (
            <div className="booking-pathway__book-online">
                <CheckForQueue setCheckedForQueue={setCheckedForQueue} />
            </div>
        )
    }

    if (isReserved == null) return <LoadingIndicator type="page" />

    return (
        <div className="booking-pathway__book-online">
            <div className="booking-pathway__select-quantity">
                <InstanceEventSummary />
                <div className="booking-pathway__select-quantity--content">

                    <div className="booking-pathway__ticket-quantity">
                        <h2 className='booking-pathway__header'>How many tickets</h2>
                        <TicketQuantityButtons ticketQuantity={ticketQuantity} setTicketQuantity={setTicketQuantity} maxTickets={maxTickets} />
                    </div>

                    <div className="booking-pathway__footer booking-pathway__select-quantity--footer">

                        { isReserved ? (
                            <>
                                { CONSTANTS.HAS_BEST_AVAILABLE ?
                                    <>
                                        <BestAvailableButton ticketQuantity={ticketQuantity} />
                                        <PurchaseByPrice bandsByPrice={bandsByPrice} ticketQuantity={ticketQuantity} />
                                    </>
                                : null}
                                
                                <button className='btn btn--select-seats' type="button" onClick={() => navigate(`/book-online/${instanceId}/select-seats`)}>
                                    Or skip to choose my own seats
                                </button>
                            </>
                        ) : (
                            <button className='btn' 
                                    type="button" 
                                    disabled={addTickets.isPending} 
                                    onClick={() => addTickets.mutateAsync()}>
                                Continue
                                {addTickets.isPending &&
                                    <LoadingIndicator />
                                }
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}